import { Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { get } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { getModel } from 'api/platform';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { modelTypeCellSegmentation } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { Permission } from 'interfaces/permissionOption';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { humanize } from 'utils/helpers';
import {
  cellSegmentationGeometryValues,
  getCellSegmentationGeometries,
  getModelId,
  hasCellSegmentationGeometry,
} from 'utils/models';
import { useModelTypeOptions } from 'utils/queryHooks/useModelTypeOptions';
import { CasesParams } from 'utils/useCasesParams/types';
import { usePermissions } from 'utils/usePermissions';
import { ChannelsController } from '../../ChannelsController';
import { SelectModelStep } from '../../SelectModelStep';
import {
  IFormValues,
  MultiplexCellSegmentationModelTypes,
  multiplexCellSegmentationModelTypesOptions,
  RunMultiplexCellSegmentationSteps,
} from './types';

export interface ConfigurationStepProps {
  casesParams: CasesParams;
  methods: UseFormReturn<IFormValues, any>;
  nuclearChannelNames: string[];
  nonNuclearChannelNames: string[];
  defaultNuclearChannelName?: string;
  checkValidationAndSetIsStepFailed: (step: RunMultiplexCellSegmentationSteps, values: IFormValues) => void;
}

export const ConfigurationStep: React.FC<ConfigurationStepProps> = ({
  casesParams,
  methods,
  nuclearChannelNames,
  nonNuclearChannelNames,
  defaultNuclearChannelName,
  checkValidationAndSetIsStepFailed,
}) => {
  const { isLoading: isLoadingDefaultModel } = useModelTypeOptions({ retry: false });

  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = methods;

  const checkStep = useCallback(
    (updates: Partial<IFormValues>) => {
      checkValidationAndSetIsStepFailed(RunMultiplexCellSegmentationSteps.Configuration, {
        ...getValues(),
        ...updates,
      });
    },
    [methods, checkValidationAndSetIsStepFailed]
  );

  const segmentationModelType = watch('modelType');
  const selectedModelPath = watch('modelPath');
  const selectedMembranalChannelNames = watch('membranalChannelNames') || [];
  const selectedModelId = getModelId(selectedModelPath || '');
  const { data: selectedModel, isLoading: isLoadingSelectedModel } = useQuery({
    queryKey: ['model', selectedModelId],
    queryFn: ({ signal }) => getModel(selectedModelId || '', signal),
    retry: false,
    enabled:
      MultiplexCellSegmentationModelTypes.amikta === segmentationModelType &&
      Boolean(selectedModelPath) &&
      Boolean(selectedModelId),
  });
  const selectedModelGeometries = getCellSegmentationGeometries(selectedModel);
  const supportsCellGeometry = hasCellSegmentationGeometry(selectedModel, cellSegmentationGeometryValues.cell);
  const isNonNuclearChannelSelectionEnabled =
    MultiplexCellSegmentationModelTypes.amikta === segmentationModelType &&
    Boolean(selectedModelPath) &&
    !isLoadingSelectedModel &&
    supportsCellGeometry;
  const shouldClearNonNuclearChannels =
    selectedMembranalChannelNames.length > 0 &&
    (MultiplexCellSegmentationModelTypes.amikta !== segmentationModelType ||
      !selectedModelPath ||
      (!isLoadingSelectedModel && !supportsCellGeometry));

  useEffect(() => {
    if (!shouldClearNonNuclearChannels) {
      return;
    }

    setValue('membranalChannelNames', [], { shouldValidate: true });
    checkStep({
      modelType: watch('modelType'),
      nuclearChannelNames: watch('nuclearChannelNames'),
      membranalChannelNames: [],
      modelPath: watch('modelPath'),
    });
  }, [checkStep, setValue, shouldClearNonNuclearChannels, watch]);

  const { hasPermission } = usePermissions();
  const canViewMultiplexCellSegmentationModelTypeSelection = hasPermission(
    Permission.ViewMultiplexCellSegmentationModelTypeSelection
  );
  const nonNuclearChannelsMessage =
    segmentationModelType !== MultiplexCellSegmentationModelTypes.amikta
      ? 'Non-nuclear channels are available only for selected models that support cell geometry.'
      : !selectedModelPath
      ? 'Select a model to see whether non-nuclear channels are available.'
      : isLoadingSelectedModel
      ? 'Loading selected model metadata for non-nuclear channels.'
      : selectedModelGeometries.length > 0 && !supportsCellGeometry
      ? 'The selected model supports nuclear geometry only.'
      : !supportsCellGeometry
      ? 'Non-nuclear channels are available only for models that support cell geometry.'
      : undefined;

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="body2" color="text.secondary">
          Choose the nuclear channel used to seed segmentation. Non-nuclear channels are available when the selected
          model supports cell geometry.
        </Typography>
      </Grid>
      {canViewMultiplexCellSegmentationModelTypeSelection && (
        <Grid item>
          <Controller
            control={control}
            name="modelType"
            render={({ field: { onChange, value } }) => (
              <LabelledDropdown
                required
                label="Model type"
                options={multiplexCellSegmentationModelTypesOptions}
                value={value ?? []}
                onOptionSelected={(optionValue) => {
                  onChange(optionValue);
                  if (MultiplexCellSegmentationModelTypes.amikta !== optionValue) {
                    setValue('modelPath', null);
                    checkStep({
                      modelType: optionValue,
                      nuclearChannelNames: watch('nuclearChannelNames'),
                      membranalChannelNames: watch('membranalChannelNames'),
                      modelPath: null,
                    });
                  } else {
                    checkStep({
                      modelType: optionValue,
                      nuclearChannelNames: watch('nuclearChannelNames'),
                      membranalChannelNames: watch('membranalChannelNames'),
                      modelPath: watch('modelPath'),
                    });
                  }
                }}
                error={Boolean(errors['modelType'])}
                helperText={humanize(errors['modelType']?.message)}
                loading={MultiplexCellSegmentationModelTypes.amikta === segmentationModelType && isLoadingDefaultModel}
              />
            )}
          />
        </Grid>
      )}
      <Grid item>
        <ChannelsController
          name="nuclearChannelNames"
          error={Boolean(get(errors, 'nuclearChannelNames'))}
          helperText={get(errors, 'nuclearChannelNames')?.message}
          methods={methods}
          allowedChannelNames={nuclearChannelNames}
          defaultSelectedChannelsOnLoad={defaultNuclearChannelName ? [defaultNuclearChannelName] : undefined}
          additionalOnChange={(optionValue) => {
            checkStep({
              modelType: watch('modelType'),
              nuclearChannelNames: optionValue,
              membranalChannelNames: watch('membranalChannelNames'),
            });
          }}
          casesParams={casesParams}
          label="Nuclear channels"
          placeholder="Select nuclear channels"
        ></ChannelsController>
      </Grid>
      <Grid item>
        <ChannelsController
          name="membranalChannelNames"
          error={Boolean(get(errors, 'membranalChannelNames'))}
          helperText={get(errors, 'membranalChannelNames')?.message}
          methods={methods}
          allowedChannelNames={nonNuclearChannelNames}
          disabled={!isNonNuclearChannelSelectionEnabled}
          additionalOnChange={(optionValue) => {
            checkStep({
              modelType: watch('modelType'),
              nuclearChannelNames: watch('nuclearChannelNames'),
              membranalChannelNames: optionValue,
            });
          }}
          casesParams={casesParams}
          label="Non-nuclear channels"
          placeholder="Select non-nuclear channels"
        ></ChannelsController>
      </Grid>
      {nonNuclearChannelsMessage ? (
        <Grid item>
          <Typography variant="body2" color="text.secondary">
            {nonNuclearChannelsMessage}
          </Typography>
        </Grid>
      ) : null}
      {MultiplexCellSegmentationModelTypes.amikta === segmentationModelType && (
        <Grid item>
          <SelectModelStep<IFormValues, 'modelPath'>
            casesParams={casesParams}
            formMethods={methods}
            modelUrlField="modelPath"
            onSelectModelUrl={(modelPath) => {
              checkStep({
                modelType: watch('modelType'),
                nuclearChannelNames: watch('nuclearChannelNames'),
                membranalChannelNames: watch('membranalChannelNames'),
                modelPath,
              });
            }}
            modelType={modelTypeCellSegmentation.apiModelValue}
            slideStain={MULTIPLEX_STAIN_ID}
          />
        </Grid>
      )}
    </Grid>
  );
};
